import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { useNavigate } from 'react-router-dom'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import UploadImage from '@/common/components/UploadImage'
import { Editor } from '@/common/components/Editor'
import { getEventBannerUploadFileUrl } from '../../api/services'

const formSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกชื่อกิจกรรม'),
	bannerUrl: z.string().optional().nullable(),
	shortDescription: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	linkUrl: z.string().optional().nullable(),
})

export type FormValues = z.infer<typeof formSchema>

interface Props {
	defaultValues?: Partial<FormValues>
	onSubmit: (values: FormValues) => void
	mode: 'create' | 'edit'
}

export function EventForm({ defaultValues, onSubmit, mode }: Props) {
	const navigate = useNavigate()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			title: defaultValues?.title || '',
			bannerUrl: defaultValues?.bannerUrl || null,
			shortDescription: defaultValues?.shortDescription || null,
			description: defaultValues?.description || null,
			linkUrl: defaultValues?.linkUrl || null,
		},
	})

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const isValid = await form.trigger()
		if (!isValid) return
		const values = form.getValues()
		onSubmit(values)
	}

	return (
		<Form {...form}>
			<form onSubmit={handleSubmit} className="space-y-8">
				<div className="space-y-8">
					{/* Basic Information */}
					<div className="rounded-lg border p-6">
						<h2 className="mb-6 text-xl font-semibold">ข้อมูลพื้นฐาน</h2>
						<div className="space-y-6">
							<FormField
								control={form.control}
								name="title"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> ชื่อกิจกรรม
										</FormLabel>
										<FormControl>
											<Input placeholder="กรอกชื่อกิจกรรม" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="shortDescription"
								render={({ field }) => (
									<FormItem>
										<FormLabel>คำอธิบายสั้น</FormLabel>
										<FormControl>
											<Textarea
												placeholder="กรอกคำอธิบายสั้นของกิจกรรม"
												className="min-h-[100px]"
												{...field}
												value={field.value || ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>รายละเอียดกิจกรรม</FormLabel>
										<FormControl>
											<Editor
												value={field.value || ''}
												onChange={field.onChange}
												placeholder="กรอกรายละเอียดกิจกรรม"
												className="min-h-[200px] [&_.ql-container]:!min-h-[150px]"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="linkUrl"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ลิงก์กิจกรรม</FormLabel>
										<FormControl>
											<Input
												placeholder="กรอกลิงก์กิจกรรม (ถ้ามี)"
												{...field}
												value={field.value || ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Media Section */}
					<div className="rounded-lg border p-6">
						<h2 className="mb-6 text-xl font-semibold">รูปภาพ</h2>
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
							<FormField
								control={form.control}
								name="bannerUrl"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Banner กิจกรรม{' '}
											<span className="text-amber-600 dark:text-amber-400">
												(สัดส่วน 16:9)
											</span>
										</FormLabel>
										<FormControl>
											<UploadImage
												onImageUploaded={(fileUrl) => field.onChange(fileUrl)}
												defaultValue={field.value ?? undefined}
												getFileUrl={({ payload }) =>
													getEventBannerUploadFileUrl({
														payload: { fileExtension: payload.fileExtension },
													})
												}
												aspectRatio="16/9"
												previewHeight={200}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex items-center gap-4 pb-8">
						<Button type="submit" variant="default" className="w-full">
							{mode === 'create' ? 'สร้างกิจกรรม' : 'แก้ไขกิจกรรม'}
						</Button>
						<Button
							type="button"
							variant="outline"
							className="w-full"
							onClick={() => navigate(-1)}
						>
							ยกเลิก
						</Button>
					</div>
				</div>
			</form>
		</Form>
	)
}
